import { Box, Heading, TableSidePanel, TabMenu, TabMenuItem } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./asset-side-panel.scss";
import { AssetGradingView } from "components/asset-management-system/asset-table/asset-table-side-panel/tabs/AssetGradingView";
import AssetErasureHistoryView from "components/asset-management-system/AssetErasureHistoryView";
import { AssetHistoryView } from "components/asset-management-system/AssetHistoryView";
import { AssetRiskView } from "components/asset-management-system/AssetRiskView";
import { GradeBadge } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { useParentAssetId } from "components/asset-management-system/hooks/useParentAssetId";
import ErasureReportModal from "components/reports/ErasureReportModal";
import { AssetPathKey, resolveAssetPathKey } from "domain/asset-management/assetPathMapping";
import {
    ASSET_IDENTITY_AND_CLASSIFICATION,
    ASSET_OVERVIEW,
    ASSET_RISK_AND_LIFECYCLE,
    AUTOPILOT,
    BATTERY,
    FINANCIAL_AND_PROCUREMENT,
    formatDurationSeconds,
    GRADING_ACTION_DETAIL_KEY,
    HARDWARE_SPECIFICATIONS,
    NETWORK_AND_IDENTITY,
    SECURITY_AND_ENDPOINT_HEALTH,
    STORAGE,
} from "domain/asset-management/assets";
import { Asset } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { formatTimestamp } from "utils/format";

import testIds from "testIds.json";

interface FieldConfiguration {
    key: AssetPathKey;
    field: AssetPathKey;
}

interface SectionConfiguration {
    titleKey: string;
    fields: FieldConfiguration[];
}

const STATUS_BADGE_KEYS = new Set<AssetPathKey>([AssetPathKey.ASSET_STATE, AssetPathKey.WARRANTY_STATUS]);

const SECTIONS: SectionConfiguration[] = [
    {
        titleKey: "AssetManagement.assetOverview",
        fields: ASSET_OVERVIEW.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.riskAndLifecycleStatus",
        fields: ASSET_RISK_AND_LIFECYCLE.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.identityAndClassification",
        fields: ASSET_IDENTITY_AND_CLASSIFICATION.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.cpuAndMemory",
        fields: HARDWARE_SPECIFICATIONS.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.storage",
        fields: STORAGE.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.battery",
        fields: BATTERY.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.networkAndIdentity",
        fields: NETWORK_AND_IDENTITY.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.autopilotAndMdm",
        fields: AUTOPILOT.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.securityAndEndpointHealth",
        fields: SECURITY_AND_ENDPOINT_HEALTH.map((field) => ({ key: field, field })),
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.dataSecurityAndErasure",
        fields: [{ key: AssetPathKey.LAST_ERASURE_DATE, field: AssetPathKey.LAST_ERASURE_DATE }],
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.warrantyAndSupport",
        fields: [{ key: AssetPathKey.WARRANTY_STATUS, field: AssetPathKey.WARRANTY_STATUS }],
    },
    {
        titleKey: "AssetManagement.sidePanel.sections.financialAndProcurement",
        fields: FINANCIAL_AND_PROCUREMENT.map((field) => ({ key: field, field })),
    },
];

const getSectionByTitleKey = (titleKey: string): SectionConfiguration => {
    const section = SECTIONS.find((s) => s.titleKey === titleKey);
    if (!section) {
        throw new Error(`Section not found for titleKey: ${titleKey}`);
    }
    return section;
};

type TabType = "details" | "risk" | "grading" | "reports" | "history";

const SECTIONS_BY_TAB: Record<TabType, SectionConfiguration[]> = {
    details: [
        getSectionByTitleKey("AssetManagement.assetOverview"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.riskAndLifecycleStatus"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.identityAndClassification"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.cpuAndMemory"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.storage"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.battery"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.networkAndIdentity"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.autopilotAndMdm"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.securityAndEndpointHealth"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.dataSecurityAndErasure"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.warrantyAndSupport"),
        getSectionByTitleKey("AssetManagement.sidePanel.sections.financialAndProcurement"),
    ],
    risk: [getSectionByTitleKey("AssetManagement.sidePanel.sections.riskAndLifecycleStatus")],
    grading: [],
    reports: [getSectionByTitleKey("AssetManagement.sidePanel.sections.dataSecurityAndErasure")],
    history: [getSectionByTitleKey("AssetManagement.sidePanel.sections.warrantyAndSupport")],
};

const DETAILS_SECTION_FIELD_KEYS = new Set<AssetPathKey>(
    SECTIONS_BY_TAB.details.flatMap((section) => section.fields.map(({ field }) => field))
);

interface AssetSidePanelProps {
    asset: Asset;
    onClose: () => void;
    isLoading: boolean;
    onAssetUpdated?: (asset: Asset) => void;
}

export function AssetSidePanel(props: AssetSidePanelProps): JSX.Element {
    const { t } = useTranslation();

    const hasBlanccoGrade = Boolean(props.asset?.[AssetPathKey.BLANCCO_GRADE]);
    const tabItems: { label: string; isVisible?: boolean }[] = [
        {
            label: t("AssetManagement.sidePanel.tabs.details"),
        },
        {
            label: t("AssetManagement.sidePanel.tabs.risk"),
        },
        {
            label: t("AssetManagement.sidePanel.tabs.grading"),
            isVisible: hasBlanccoGrade,
        },
        {
            label: t("AssetManagement.sidePanel.tabs.erasureReports"),
        },
        {
            label: t("AssetManagement.sidePanel.tabs.assetHistory"),
        },
    ];
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const [activeTab, setActiveTab] = useState<number>(0);
    const [toViewReport, setToViewReport] = React.useState(false);
    const [reportDate, setReportDate] = React.useState<string>("");
    const [reportUuid, setReportUuid] = React.useState<string>("");
    const parentAssetId = useParentAssetId(props.asset?.parentUuid);

    useEffect(() => {
        setActiveTab(0);
    }, [props.asset?.uuid]);

    const getFieldValue = (asset: Asset, key: AssetPathKey): string | undefined => {
        return asset[key] == null ? undefined : String(asset[key]);
    };

    const renderFieldValue = (key: AssetPathKey, value: string): JSX.Element | string => {
        if (STATUS_BADGE_KEYS.has(key)) {
            return <span className={style.statusBadge}>{value}</span>;
        }
        if (key === AssetPathKey.CREATED_AT) {
            return formatTimestamp(value);
        }
        if (key === AssetPathKey.BLANCCO_ERASABILITY && value) {
            return value + "%";
        }
        if (key === AssetPathKey.BLANCCO_DURATION && value) {
            return formatDurationSeconds(Number(value));
        }
        if (key === AssetPathKey.BLANCCO_GRADE && value) {
            return <GradeBadge grade={value as "A" | "B" | "C" | "D"} size={24} />;
        }
        return value;
    };

    const renderSection = (section: SectionConfiguration): JSX.Element | null => {
        if (!props.asset) {
            return null;
        }

        const visibleFields = section.fields.filter(({ key }) => {
            const value = getFieldValue(props.asset!, key);
            return value !== undefined && value !== null && value !== "";
        });

        if (visibleFields.length === 0) {
            return null;
        }

        return (
            <Box columnLayout key={section.titleKey} className={style.section}>
                <hr className={style.sectionDivider} />
                <Heading variant="SUBTITLE_2" className={style.sectionTitle}>
                    {t(section.titleKey)}
                </Heading>
                {visibleFields.map(({ key, field }, index) => (
                    <Box key={`${String(key)}-${index}`} className={style.detailRow} gap="Xxs" marginBottom="Xs">
                        <span className={style.detailLabel}>{translatedPaths(field)}</span>
                        <span className={style.detailValue}>
                            {renderFieldValue(key, getFieldValue(props.asset!, key) ?? "")}
                        </span>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderCustomFields = (): JSX.Element | null => {
        const unknownFields = (props.asset?.assetDetails ?? []).filter(({ key }) => {
            if (key === GRADING_ACTION_DETAIL_KEY) {
                return false;
            }
            if (key === AssetPathKey.OPTIMIZATION_SUGGESTIONS) {
                return false;
            }
            const resolvedKey = resolveAssetPathKey(key);
            if (!resolvedKey) {
                return true;
            }
            return !DETAILS_SECTION_FIELD_KEYS.has(resolvedKey);
        });

        const isParentAsset = props.asset && (props.asset.hasChildren || props.asset.hasHiddenChildren);
        const showParentAssetId = !isParentAsset && parentAssetId;
        const hasParentAssetId =
            showParentAssetId || (props.asset?.assetDetails ?? []).some((d) => d.key === "parent_asset_id");
        if (unknownFields.length === 0 && !hasParentAssetId) {
            return null;
        }
        return (
            <Box columnLayout marginBottom="Sm" className={style.section}>
                <hr className={style.sectionDivider} />
                <Heading variant="SUBTITLE_2" className={style.sectionTitle}>
                    {t("AssetManagement.sidePanel.sections.additionalFields")}
                </Heading>
                {showParentAssetId && (
                    <Box className={style.detailRow} gap="Xxs" marginBottom="Xs">
                        <span className={style.detailLabel}>{translatedPaths("parent_asset_id")}</span>
                        <span className={style.detailValue}>{parentAssetId}</span>
                    </Box>
                )}
                {unknownFields.map(({ key, value }, index) => {
                    const resolvedKey = resolveAssetPathKey(key);
                    const label = resolvedKey ? translatedPaths(resolvedKey) : key;

                    return (
                        <Box key={`additional-${key}-${index}`} className={style.detailRow} gap="Xxs" marginBottom="Xs">
                            <span className={style.detailLabel}>{label}</span>
                            <span className={style.detailValue}>
                                {resolvedKey ? renderFieldValue(resolvedKey, value) : value}
                            </span>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <TableSidePanel
            heading={props.asset?.assetId}
            onClose={props.onClose}
            testIds={{
                tableSidePanelTestId: testIds.workArea.asset.sidePanel.itself,
                closeButtonTestId: testIds.workArea.asset.sidePanel.closeButton,
            }}
            className={style.tabSidePanel}
            isLoading={props.isLoading}
        >
            <TabMenu className={classNames({ [style.tabMenu]: activeTab !== 1 })}>
                {tabItems.map(
                    (tab, index) =>
                        tab.isVisible !== false && (
                            <TabMenuItem key={index} isActive={activeTab === index} onClick={() => setActiveTab(index)}>
                                {tab.label}
                            </TabMenuItem>
                        )
                )}
            </TabMenu>

            {activeTab === 0 && (
                <div className={style.tabContent}>
                    {SECTIONS_BY_TAB["details"].map(renderSection)}
                    {renderCustomFields()}
                </div>
            )}
            {activeTab === 1 && <AssetRiskView assetUuid={props.asset.uuid} />}
            {activeTab === 2 && hasBlanccoGrade && (
                <AssetGradingView
                    assetUuid={props.asset.uuid}
                    asset={props.asset}
                    onAssetUpdated={props.onAssetUpdated}
                />
            )}
            {activeTab === 3 && (
                <AssetErasureHistoryView
                    assetUuid={props.asset.uuid}
                    assetIdentifier={props.asset.assetId}
                    setToViewReport={setToViewReport}
                    setReportUuid={setReportUuid}
                    setReportDate={setReportDate}
                />
            )}
            {activeTab === 4 && <AssetHistoryView assetUuid={props.asset.uuid} />}
            {toViewReport && (
                <ErasureReportModal
                    reportUuid={reportUuid}
                    date={reportDate}
                    hide={() => setToViewReport(false)}
                    openInNewTab={() => {}}
                    onReportDelete={() => {}}
                />
            )}
        </TableSidePanel>
    );
}

export default AssetSidePanel;
